import { useEffect } from "react";
import { SEOHead } from "@/components/SEOHead";
import { Button } from "@/components/ui/button";
import {
  CheckCircle2,
  Shield,
  Clock,
  MapPin,
  Phone,
  Star,
  ArrowRight,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTrigger,
  DialogTitle,
  DialogDescription,
  DialogHeader,
} from "@/components/ui/dialog";
import { ContactForm } from "@/components/ContactForm";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { OurWorkSection } from "@/components/OurWorkSection";

const getPricingContent = (location: string) => {
  if (location.includes("Berlin")) {
    return (
      <div className="space-y-6 text-lg text-muted-foreground">
        <p>
          In der Hauptstadt <strong>Berlin</strong> hängen die Kosten einer
          Haushaltsauflösung von vielen Faktoren ab – von der Etage bis zur
          Parksituation im Kiez. Wir bieten Ihnen transparente und faire Preise
          ab 199 €.
        </p>
        <div className="flex items-start gap-4">
          <CheckCircle2 className="h-6 w-6 text-primary shrink-0 mt-1" />
          <div>
            <strong className="text-foreground">Typische Faktoren:</strong> Ein
            Altbau ohne Aufzug in Mitte erfordert mehr Aufwand als eine
            ebenerdige Wohnung. Unser Festpreis berücksichtigt diese Berliner
            Eigenheiten im Vorfeld.
          </div>
        </div>
        <div className="flex items-start gap-4">
          <CheckCircle2 className="h-6 w-6 text-primary shrink-0 mt-1" />
          <div>
            <strong className="text-foreground">Planungssicherheit:</strong> Wir
            kümmern uns um Halteverbotszonen und die fachgerechte Entsorgung auf
            Berliner Recyclinghöfen – alles im Preis inklusive.
          </div>
        </div>
        <p className="font-medium text-foreground">
          <strong>Unser Tipp:</strong> Fragen Sie einfach kostenfrei und
          unverbindlich bei uns an. Anhand Ihrer Angaben stellen wir Ihnen ein
          transparentes Angebot für Ihre Haushaltsauflösung in Berlin zusammen.
        </p>
      </div>
    );
  }
  if (location.includes("Pankow")) {
    return (
      <div className="space-y-6 text-lg text-muted-foreground">
        <p>
          Für eine Haushaltsauflösung in <strong>Pankow</strong> kalkulieren wir
          die Kosten individuell und fair. Egal ob im dicht bebauten Prenzlauer
          Berg oder im ruhigeren Niederschönhausen.
        </p>
        <div className="flex items-start gap-4">
          <CheckCircle2 className="h-6 w-6 text-primary shrink-0 mt-1" />
          <div>
            <strong className="text-foreground">Beispiel:</strong> Eine
            Wohnungsauflösung in einem Pankower Mehrfamilienhaus. Wir bewerten
            die Menge des Hausrats und mögliche Wertgegenstände, die den Preis
            senken können.
          </div>
        </div>
        <div className="flex items-start gap-4">
          <CheckCircle2 className="h-6 w-6 text-primary shrink-0 mt-1" />
          <div>
            <strong className="text-foreground">
              Keine versteckten Kosten:
            </strong>{" "}
            Unser Angebot für Pankow ist ein garantierter Festpreis. Abbau,
            Transport und umweltgerechte Entsorgung sind komplett abgedeckt.
          </div>
        </div>
        <p className="font-medium text-foreground">
          <strong>Unser Tipp:</strong> Fragen Sie einfach kostenfrei und
          unverbindlich bei uns an. Anhand Ihrer Angaben stellen wir Ihnen ein
          transparentes Angebot für Ihre Haushaltsauflösung in Pankow zusammen.
        </p>
      </div>
    );
  }
  if (location.includes("Oberhavel")) {
    return (
      <div className="space-y-6 text-lg text-muted-foreground">
        <p>
          Im Landkreis <strong>Oberhavel</strong> sind die Wege oft weiter und
          die Grundstücke größer. Wir passen unsere Preisgestaltung an die
          regionalen Gegebenheiten an, um Ihnen das beste Angebot zu machen.
        </p>
        <div className="flex items-start gap-4">
          <CheckCircle2 className="h-6 w-6 text-primary shrink-0 mt-1" />
          <div>
            <strong className="text-foreground">Häuser und Grundstücke:</strong>{" "}
            Neben Wohnungen räumen wir in Oberhavel häufig auch
            Einfamilienhäuser mit Garagen, Schuppen und großen Kellern. Das
            fließt in unsere transparente Kalkulation ein.
          </div>
        </div>
        <div className="flex items-start gap-4">
          <CheckCircle2 className="h-6 w-6 text-primary shrink-0 mt-1" />
          <div>
            <strong className="text-foreground">Wertanrechnung:</strong>{" "}
            Gefundene Antiquitäten oder verwertbare Möbel rechnen wir fair an,
            was Ihre Gesamtkosten in Oberhavel deutlich reduzieren kann.
          </div>
        </div>
        <p className="font-medium text-foreground">
          <strong>Unser Tipp:</strong> Fragen Sie einfach kostenfrei und
          unverbindlich bei uns an. Anhand Ihrer Angaben stellen wir Ihnen ein
          transparentes Angebot für Ihre Haushaltsauflösung in Oberhavel
          zusammen.
        </p>
      </div>
    );
  }
  if (location.includes("Marzahn") || location.includes("Hellersdorf")) {
    return (
      <div className="space-y-6 text-lg text-muted-foreground">
        <p>
          In <strong>Marzahn-Hellersdorf</strong> prägen sowohl Plattenbauten
          als auch Einfamilienhaus-Siedlungen das Bild. Unsere Preise für eine
          Haushaltsauflösung sind so flexibel wie der Bezirk selbst.
        </p>
        <div className="flex items-start gap-4">
          <CheckCircle2 className="h-6 w-6 text-primary shrink-0 mt-1" />
          <div>
            <strong className="text-foreground">Aufzüge nutzen:</strong> In den
            Hochhäusern von Marzahn erleichtert ein vorhandener Aufzug die
            Arbeit enorm – diesen Zeitvorteil geben wir bei der Preisgestaltung
            direkt an Sie weiter.
          </div>
        </div>
        <div className="flex items-start gap-4">
          <CheckCircle2 className="h-6 w-6 text-primary shrink-0 mt-1" />
          <div>
            <strong className="text-foreground">Alles aus einer Hand:</strong>{" "}
            Vom Sortieren über den Abtransport bis zur besenreinen Übergabe –
            unser Festpreis in Marzahn-Hellersdorf deckt alle Leistungen ab.
          </div>
        </div>
        <p className="font-medium text-foreground">
          <strong>Unser Tipp:</strong> Fragen Sie einfach kostenfrei und
          unverbindlich bei uns an. Anhand Ihrer Angaben stellen wir Ihnen ein
          transparentes Angebot für Ihre Haushaltsauflösung in
          Marzahn-Hellersdorf zusammen.
        </p>
      </div>
    );
  }
  if (location.includes("Treptow") || location.includes("Köpenick")) {
    return (
      <div className="space-y-6 text-lg text-muted-foreground">
        <p>
          Der wasser- und waldreiche Bezirk <strong>Treptow-Köpenick</strong>{" "}
          erfordert bei Haushaltsauflösungen oft besondere Logistik. Wir bieten
          Ihnen hierfür faire und nachvollziehbare Preise.
        </p>
        <div className="flex items-start gap-4">
          <CheckCircle2 className="h-6 w-6 text-primary shrink-0 mt-1" />
          <div>
            <strong className="text-foreground">
              Individuelle Kalkulation:
            </strong>{" "}
            Ob eine Wohnung in Adlershof oder ein Haus in Müggelheim – wir
            berechnen die Kosten exakt nach Aufwand, Volumen und Zugänglichkeit.
          </div>
        </div>
        <div className="flex items-start gap-4">
          <CheckCircle2 className="h-6 w-6 text-primary shrink-0 mt-1" />
          <div>
            <strong className="text-foreground">Nachhaltigkeit:</strong> Auch in
            Treptow-Köpenick setzen wir auf umweltgerechte Entsorgung und
            Recycling, was nicht nur die Umwelt schont, sondern auch
            Entsorgungskosten optimiert.
          </div>
        </div>
        <p className="font-medium text-foreground">
          <strong>Unser Tipp:</strong> Fragen Sie einfach kostenfrei und
          unverbindlich bei uns an. Anhand Ihrer Angaben stellen wir Ihnen ein
          transparentes Angebot für Ihre Haushaltsauflösung in Treptow-Köpenick
          zusammen.
        </p>
      </div>
    );
  }
  if (location.includes("Spandau")) {
    return (
      <div className="space-y-6 text-lg text-muted-foreground">
        <p>
          Im traditionsreichen <strong>Spandau</strong> führen wir regelmäßig
          Haushaltsauflösungen durch. Unsere Preise sind dabei stets transparent
          und fair kalkuliert.
        </p>
        <div className="flex items-start gap-4">
          <CheckCircle2 className="h-6 w-6 text-primary shrink-0 mt-1" />
          <div>
            <strong className="text-foreground">Umfassender Service:</strong>{" "}
            Von der kleinen Wohnung in der Altstadt bis zum Einfamilienhaus in
            Kladow – wir erstellen für jedes Objekt in Spandau ein passgenaues
            Festpreisangebot.
          </div>
        </div>
        <div className="flex items-start gap-4">
          <CheckCircle2 className="h-6 w-6 text-primary shrink-0 mt-1" />
          <div>
            <strong className="text-foreground">Ihre Sicherheit:</strong> Mit
            unserem Festpreis für Spandau sind Sie vor nachträglichen
            Preiserhöhungen geschützt. Alle Kostenpunkte werden vorab klar
            kommuniziert.
          </div>
        </div>
        <p className="font-medium text-foreground">
          <strong>Unser Tipp:</strong> Fragen Sie einfach kostenfrei und
          unverbindlich bei uns an. Anhand Ihrer Angaben stellen wir Ihnen ein
          transparentes Angebot für Ihre Haushaltsauflösung in Spandau zusammen.
        </p>
      </div>
    );
  }
  if (location.includes("Reinickendorf")) {
    return (
      <div className="space-y-6 text-lg text-muted-foreground">
        <p>
          In <strong>Reinickendorf</strong> – vom Märkischen Viertel bis Frohnau
          – bieten wir Haushaltsauflösungen zu festen, verlässlichen Preisen an.
        </p>
        <div className="flex items-start gap-4">
          <CheckCircle2 className="h-6 w-6 text-primary shrink-0 mt-1" />
          <div>
            <strong className="text-foreground">Lokale Expertise:</strong> Dank
            unserer Erfahrung in Reinickendorf können wir Aufwand und Kosten
            sehr präzise einschätzen, was Ihnen einen fairen Preis garantiert.
          </div>
        </div>
        <div className="flex items-start gap-4">
          <CheckCircle2 className="h-6 w-6 text-primary shrink-0 mt-1" />
          <div>
            <strong className="text-foreground">Kostentransparenz:</strong> Sie
            erhalten nach der kostenlosen Besichtigung ein verbindliches
            Angebot. So wissen Sie genau, was die Entrümpelung in Reinickendorf
            kosten wird.
          </div>
        </div>
        <p className="font-medium text-foreground">
          <strong>Unser Tipp:</strong> Fragen Sie einfach kostenfrei und
          unverbindlich bei uns an. Anhand Ihrer Angaben stellen wir Ihnen ein
          transparentes Angebot für Ihre Haushaltsauflösung in Reinickendorf
          zusammen.
        </p>
      </div>
    );
  }
  if (location.includes("Neukölln")) {
    return (
      <div className="space-y-6 text-lg text-muted-foreground">
        <p>
          Der lebhafte Bezirk <strong>Neukölln</strong> stellt bei
          Haushaltsauflösungen oft logistische Herausforderungen. Dennoch
          garantieren wir Ihnen faire und transparente Preise.
        </p>
        <div className="flex items-start gap-4">
          <CheckCircle2 className="h-6 w-6 text-primary shrink-0 mt-1" />
          <div>
            <strong className="text-foreground">Logistik inklusive:</strong>{" "}
            Enge Treppenhäuser und wenig Parkraum im Nordneuköllner Kiez? Wir
            planen Halteverbotszonen und Laufwege direkt in unser
            Neukölln-Angebot ein.
          </div>
        </div>
        <div className="flex items-start gap-4">
          <CheckCircle2 className="h-6 w-6 text-primary shrink-0 mt-1" />
          <div>
            <strong className="text-foreground">Zuverlässigkeit:</strong> Unser
            Festpreis bedeutet für Sie: Keine Nachverhandlungen. Wir räumen Ihre
            Wohnung in Neukölln professionell und zum vereinbarten Preis.
          </div>
        </div>
        <p className="font-medium text-foreground">
          <strong>Unser Tipp:</strong> Fragen Sie einfach kostenfrei und
          unverbindlich bei uns an. Anhand Ihrer Angaben stellen wir Ihnen ein
          transparentes Angebot für Ihre Haushaltsauflösung in Neukölln
          zusammen.
        </p>
      </div>
    );
  }
  if (location.includes("Steglitz") || location.includes("Zehlendorf")) {
    return (
      <div className="space-y-6 text-lg text-muted-foreground">
        <p>
          In <strong>Steglitz-Zehlendorf</strong> treffen wir oft auf große
          Wohnungen und Villen mit viel Inventar. Wir bieten Ihnen hierfür eine
          diskrete und preislich faire Haushaltsauflösung.
        </p>
        <div className="flex items-start gap-4">
          <CheckCircle2 className="h-6 w-6 text-primary shrink-0 mt-1" />
          <div>
            <strong className="text-foreground">Wertvolle Nachlässe:</strong>{" "}
            Besonders in Zehlendorf finden sich oft Antiquitäten oder
            Kunstwerke. Diese rechnen wir transparent an, was die Kosten der
            Räumung erheblich senken kann.
          </div>
        </div>
        <div className="flex items-start gap-4">
          <CheckCircle2 className="h-6 w-6 text-primary shrink-0 mt-1" />
          <div>
            <strong className="text-foreground">Premium-Service:</strong> Unser
            Festpreis für Steglitz-Zehlendorf beinhaltet höchste Sorgfalt,
            Diskretion und eine besenreine Übergabe der Immobilie.
          </div>
        </div>
        <p className="font-medium text-foreground">
          <strong>Unser Tipp:</strong> Fragen Sie einfach kostenfrei und
          unverbindlich bei uns an. Anhand Ihrer Angaben stellen wir Ihnen ein
          transparentes Angebot für Ihre Haushaltsauflösung in
          Steglitz-Zehlendorf zusammen.
        </p>
      </div>
    );
  }
  if (location.includes("Märkisch")) {
    return (
      <div className="space-y-6 text-lg text-muted-foreground">
        <p>
          Für Haushaltsauflösungen in <strong>Märkisch-Oderland</strong>{" "}
          berechnen wir faire Preise, die die Anfahrt und die oft größeren zu
          räumenden Flächen berücksichtigen.
        </p>
        <div className="flex items-start gap-4">
          <CheckCircle2 className="h-6 w-6 text-primary shrink-0 mt-1" />
          <div>
            <strong className="text-foreground">Große Objekte:</strong> Ob
            Bauernhof, Einfamilienhaus oder Nebengebäude in Märkisch-Oderland –
            wir kalkulieren den Aufwand realistisch und bieten Ihnen einen
            Festpreis.
          </div>
        </div>
        <div className="flex items-start gap-4">
          <CheckCircle2 className="h-6 w-6 text-primary shrink-0 mt-1" />
          <div>
            <strong className="text-foreground">Rundum-Sorglos:</strong> Sie
            müssen sich um nichts kümmern. Unser Preis umfasst den kompletten
            Prozess von der Räumung bis zur fachgerechten Entsorgung.
          </div>
        </div>
        <p className="font-medium text-foreground">
          <strong>Unser Tipp:</strong> Fragen Sie einfach kostenfrei und
          unverbindlich bei uns an. Anhand Ihrer Angaben stellen wir Ihnen ein
          transparentes Angebot für Ihre Haushaltsauflösung in Märkisch-Oderland
          zusammen.
        </p>
      </div>
    );
  }
  if (location.includes("Charlottenburg")) {
    return (
      <div className="space-y-6 text-lg text-muted-foreground">
        <p>
          In <strong>Charlottenburg</strong> räumen wir regelmäßig
          herrschaftliche Altbauwohnungen und Gewerbeobjekte. Unsere Preise
          spiegeln unseren hohen Qualitätsanspruch wider, bleiben dabei aber
          fair.
        </p>
        <div className="flex items-start gap-4">
          <CheckCircle2 className="h-6 w-6 text-primary shrink-0 mt-1" />
          <div>
            <strong className="text-foreground">Sorgfalt im Altbau:</strong> Der
            Schutz von Parkettböden und historischen Treppenhäusern in
            Charlottenburg ist in unserem Service und Festpreis
            selbstverständlich inbegriffen.
          </div>
        </div>
        <div className="flex items-start gap-4">
          <CheckCircle2 className="h-6 w-6 text-primary shrink-0 mt-1" />
          <div>
            <strong className="text-foreground">Wertanrechnung:</strong>{" "}
            Hochwertiges Mobiliar oder Designklassiker, die wir bei der
            Auflösung in Charlottenburg finden, werden fair bewertet und
            kostenmindernd angerechnet.
          </div>
        </div>
        <p className="font-medium text-foreground">
          <strong>Unser Tipp:</strong> Fragen Sie einfach kostenfrei und
          unverbindlich bei uns an. Anhand Ihrer Angaben stellen wir Ihnen ein
          transparentes Angebot für Ihre Haushaltsauflösung in Charlottenburg
          zusammen.
        </p>
      </div>
    );
  }
  if (location.includes("Potsdam")) {
    return (
      <div className="space-y-6 text-lg text-muted-foreground">
        <p>
          Auch in der Landeshauptstadt <strong>Potsdam</strong> bieten wir
          unsere professionellen Haushaltsauflösungen zu verbindlichen
          Festpreisen an.
        </p>
        <div className="flex items-start gap-4">
          <CheckCircle2 className="h-6 w-6 text-primary shrink-0 mt-1" />
          <div>
            <strong className="text-foreground">Transparenz:</strong> Egal ob in
            der historischen Innenstadt oder in Babelsberg – wir besichtigen das
            Objekt in Potsdam kostenlos und nennen Ihnen einen verlässlichen
            Preis.
          </div>
        </div>
        <div className="flex items-start gap-4">
          <CheckCircle2 className="h-6 w-6 text-primary shrink-0 mt-1" />
          <div>
            <strong className="text-foreground">Alles inklusive:</strong> Unser
            Angebot für Potsdam enthält alle Posten: Demontage, Transport,
            Entsorgungsgebühren und die besenreine Endreinigung.
          </div>
        </div>
        <p className="font-medium text-foreground">
          <strong>Unser Tipp:</strong> Fragen Sie einfach kostenfrei und
          unverbindlich bei uns an. Anhand Ihrer Angaben stellen wir Ihnen ein
          transparentes Angebot für Ihre Haushaltsauflösung in Potsdam zusammen.
        </p>
      </div>
    );
  }
  if (location.includes("Oranienburg")) {
    return (
      <div className="space-y-6 text-lg text-muted-foreground">
        <p>
          Für eine reibungslose Haushaltsauflösung in{" "}
          <strong>Oranienburg</strong> stehen wir Ihnen mit fairen Konditionen
          und einem klaren Festpreis zur Seite.
        </p>
        <div className="flex items-start gap-4">
          <CheckCircle2 className="h-6 w-6 text-primary shrink-0 mt-1" />
          <div>
            <strong className="text-foreground">Faire Bewertung:</strong> Wir
            schätzen den Arbeitsaufwand für Ihr Objekt in Oranienburg
            realistisch ein und erstellen ein Angebot ohne versteckte
            Zusatzkosten.
          </div>
        </div>
        <div className="flex items-start gap-4">
          <CheckCircle2 className="h-6 w-6 text-primary shrink-0 mt-1" />
          <div>
            <strong className="text-foreground">Verlässlichkeit:</strong> Mit
            unserem Festpreis garantieren wir Ihnen finanzielle
            Planungssicherheit für Ihre Entrümpelung oder Wohnungsauflösung in
            Oranienburg.
          </div>
        </div>
        <p className="font-medium text-foreground">
          <strong>Unser Tipp:</strong> Fragen Sie einfach kostenfrei und
          unverbindlich bei uns an. Anhand Ihrer Angaben stellen wir Ihnen ein
          transparentes Angebot für Ihre Haushaltsauflösung in Oranienburg
          zusammen.
        </p>
      </div>
    );
  }

  // Fallback
  return (
    <div className="space-y-6 text-lg text-muted-foreground">
      <p>
        Wahrscheinlich die Frage, die Ihnen schon seit der Überschrift auf der
        Zunge brennt:{" "}
        <strong>
          Was kostet eine Haushaltsauflösung in {location} eigentlich?
        </strong>{" "}
        Bei Miros Haushaltsauflösung starten unsere Preise fair kalkuliert.
        Natürlich kann es je nach Größe und Aufwand variieren, aber unser
        Festpreis bleibt in jedem Fall transparent.
      </p>
      <div className="flex items-start gap-4">
        <CheckCircle2 className="h-6 w-6 text-primary shrink-0 mt-1" />
        <div>
          <strong className="text-foreground">Beispiel:</strong> Sie haben eine
          Zweizimmerwohnung mit Keller in {location}, die entrümpelt werden
          muss. Anhand Ihrer Fotos und Informationen kalkulieren wir genau und
          nennen Ihnen einen fairen Festpreis. In diesem Preis sind sämtliche
          Kosten für Abbau, Transport, Entsorgung und Personal enthalten.
        </div>
      </div>
      <div className="flex items-start gap-4">
        <CheckCircle2 className="h-6 w-6 text-primary shrink-0 mt-1" />
        <div>
          <strong className="text-foreground">
            Warum unsere Preise fair sind:
          </strong>{" "}
          Wir arbeiten effizient. Wir sind eingespielt, kennen die Anlaufstellen
          in und um {location} und müssen keine Zeit mit unnötigen Wegen
          verschwenden. Effizienz spart Kosten – für uns und damit für Sie.
        </div>
      </div>
      <p className="font-medium text-foreground">
        <strong>Unser Tipp:</strong> Fragen Sie einfach kostenfrei und
        unverbindlich bei uns an. Anhand Ihrer Angaben stellen wir Ihnen ein
        transparentes Angebot für Ihre Haushaltsauflösung in {location}{" "}
        zusammen.
      </p>
    </div>
  );
};

export interface LocationPageProps {
  location: string;
  locationSlug: string;
  keyword: string;
  metaTitle: string;
  metaDesc: string;
  heroSub: string;
  heroImg: string;
  localIntro: React.ReactNode;
  trustText: string;
  trustQuote?: string;
  challengesText: React.ReactNode;
  reviews: Array<{ text: string; author: string; img?: string }>;
  neighbors: Array<{ name: string; slug: string }>;
  faqs: Array<{ q: string; a: string }>;
}

const allServices = [
  {
    id: "haushaltsaufloesung-berlin",
    title: "Haushaltsauflösung",
    desc: "Komplette Räumung vom Keller bis zum Dachboden, inklusive Demontage.",
  },
  {
    id: "entruempelung-berlin",
    title: "Entrümpelung",
    desc: "Schnelle Befreiung von Sperrmüll und Unrat mit fachgerechter Entsorgung.",
  },
  {
    id: "nachlassverwertung-berlin",
    title: "Nachlassverwertung",
    desc: "Sichtung und faire Anrechnung von Wertsachen aus dem Nachlass.",
  },
  {
    id: "bueroaufloesung-berlin",
    title: "Büroauflösung",
    desc: "Demontage von IT und Möbeln sowie zertifizierte Aktenvernichtung.",
  },
  {
    id: "wohnungsaufloesung-berlin",
    title: "Wohnungsauflösung",
    desc: "Besenreine oder auf Wunsch grundgereinigte Übergabe der Räume.",
  },
  {
    id: "geschaeftsaufloesung-berlin",
    title: "Geschäftsauflösung",
    desc: "Strukturierte Räumung von Ladenlokalen und Werkstätten.",
  },
];

export function LocationPageTemplate({ page }: { page: LocationPageProps }) {
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": "https://miros-haushaltsaufloesung.de/#localbusiness",
    name: "Miros Haushaltsauflösung",
    image: page.heroImg,
    description: page.metaDesc,
    url: `https://miros-haushaltsaufloesung.de/${page.locationSlug}`,
    telephone: "+4915253181061",
    address: {
      "@type": "PostalAddress",
      streetAddress: "Hauptstraße 43-44",
      addressLocality: "Berlin",
      postalCode: "13127",
      addressCountry: "DE",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: 52.5833,
      longitude: 13.4,
    },
    areaServed: {
      "@type": "City",
      name: page.location,
    },
    openingHoursSpecification: {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: [
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
      ],
      opens: "08:00",
      closes: "20:00",
    },
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "5.0",
      reviewCount: "500",
    },
    sameAs: [
      "https://www.google.com/maps/place/Miros+Haushaltsaufl%C3%B6sung/@52.7890372,12.4843482,9z/data=!3m1!4b1!4m6!3m5!1s0x23bf4a60473c084d:0x8a12d3b416c3d36a!8m2!3d52.7890372!4d12.4843482!16s%2Fg%2F11w12b1_n0",
    ],
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Startseite",
        item: "https://miros-haushaltsaufloesung.de/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: `Haushaltsauflösung ${page.location}`,
        item: `https://miros-haushaltsaufloesung.de/${page.locationSlug}`,
      },
    ],
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: page.faqs.map((faq) => ({
      "@type": "Question",
      name: faq.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.a,
      },
    })),
  };

  const schemas = [localBusinessSchema, breadcrumbSchema, faqSchema];

  return (
    <>
      <SEOHead
        title={page.metaTitle}
        description={page.metaDesc}
        canonical={`https://miros-haushaltsaufloesung.de/${page.locationSlug}`}
        schemaJson={schemas}
        ogImage={page.heroImg}
      />
      <div className="flex flex-col w-full">
        {/* 1. HERO SECTION */}
        <section className="relative w-full pt-32 md:pt-40 flex items-stretch bg-[#0c2340] text-white overflow-hidden min-h-[70vh]">
          <div className="absolute inset-0 z-0">
            <img
              src={page.heroImg}
              alt={page.keyword}
              width="1200"
              height="800"
              className="w-full h-full object-cover opacity-30"
              loading="eager"
              fetchPriority="high"
              decoding="async"
            />
            <div className="absolute inset-0 bg-[#0c2340]/70"></div>
          </div>

          <div className="container relative z-20 mx-auto px-4 md:px-6 h-full flex flex-col justify-end pt-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-end h-full">
              <div className="text-left space-y-8 pb-20 md:pb-32">
                <nav
                  aria-label="Breadcrumb"
                  className="mb-4 animate-in fade-in slide-in-from-bottom-4 duration-700"
                >
                  <ol className="flex items-center space-x-2 text-sm text-white/70">
                    <li>
                      <a
                        href="/"
                        className="hover:text-white transition-colors"
                      >
                        Startseite
                      </a>
                    </li>
                    <li>
                      <span className="mx-2">/</span>
                    </li>
                    <li>
                      <a
                        href="/#einsatzgebiete"
                        className="hover:text-white transition-colors"
                      >
                        Servicegebiete
                      </a>
                    </li>
                    <li>
                      <span className="mx-2">/</span>
                    </li>
                    <li className="text-white font-medium" aria-current="page">
                      {page.location}
                    </li>
                  </ol>
                </nav>

                <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-black text-white leading-[1.1] tracking-tight animate-in fade-in slide-in-from-bottom-4 duration-700">
                  {page.keyword}
                </h1>

                <p className="text-lg md:text-xl text-white/90 font-medium animate-in fade-in slide-in-from-bottom-4 duration-700 delay-100">
                  {page.heroSub}
                </p>

                <div className="flex flex-col sm:flex-row gap-6 animate-in fade-in slide-in-from-bottom-4 duration-700 delay-200">
                  <div className="flex items-center gap-3">
                    <CheckCircle2 className="h-6 w-6 text-accent shrink-0" />
                    <span className="font-bold">Festpreisgarantie</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Shield className="h-6 w-6 text-accent shrink-0" />
                    <span className="font-bold">
                      Betriebshaftpflicht versichert
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Clock className="h-6 w-6 text-accent shrink-0" />
                    <span className="font-bold">
                      Termine innerhalb 48 Stunden
                    </span>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row items-center gap-6 animate-in fade-in slide-in-from-bottom-4 duration-700 delay-300">
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button
                        size="lg"
                        className="h-14 px-8 text-lg font-bold bg-accent text-accent-foreground hover:bg-accent/90 rounded-md shadow-lg hover:shadow-xl w-full sm:w-auto"
                      >
                        Kostenlose Besichtigung anfragen
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="w-[95vw] max-w-[500px] p-0 overflow-hidden bg-background sm:rounded-xl">
                      <div className="p-5 sm:p-8 max-h-[85vh] overflow-y-auto">
                        <DialogHeader className="mb-5">
                          <DialogTitle className="text-xl sm:text-2xl font-bold">
                            Kostenloses Angebot anfordern
                          </DialogTitle>
                          <DialogDescription className="text-sm sm:text-base">
                            Hinterlassen Sie uns Ihre Details und wir melden uns
                            umgehend bei Ihnen.
                          </DialogDescription>
                        </DialogHeader>
                        <ContactForm />
                      </div>
                    </DialogContent>
                  </Dialog>
                  <a
                    href="tel:015253181061"
                    className="flex items-center gap-2 text-xl font-bold text-white hover:text-accent transition-colors"
                  >
                    <Phone className="h-6 w-6" /> 01525 3181061
                  </a>
                </div>
              </div>

              <div className="relative mx-auto w-full max-w-md lg:max-w-none flex justify-center lg:justify-end items-end h-full">
                <div className="relative inline-block w-full max-w-[550px] lg:max-w-[750px]">
                  <img
                    src="https://assets.cdn.filesafe.space/RcAk4NqqJiBX4hgBLewG/media/6a3d1c2ddb2129be182b6f09.png"
                    alt="Miroslav Ozegovic"
                    width="400"
                    height="600"
                    className="w-full h-auto object-contain animate-in fade-in slide-in-from-bottom-8 duration-1000 delay-300 relative z-10"
                    loading="eager"
                    fetchPriority="high"
                    decoding="async"
                  />
                  <div className="absolute bottom-4 sm:bottom-8 left-1/2 transform -translate-x-1/2 w-[95%] max-w-[340px] bg-[#0f172a]/70 backdrop-blur-md border border-white/10 rounded-xl p-4 sm:p-5 text-center shadow-2xl animate-in fade-in slide-in-from-bottom-4 duration-700 delay-700 z-20">
                    <div className="text-white font-bold text-lg sm:text-xl mb-1">
                      Darf ich Sie beraten?
                    </div>
                    <a
                      href="tel:015253181061"
                      className="text-accent font-black text-2xl sm:text-3xl tracking-tight block hover:scale-105 transition-transform duration-200"
                    >
                      01525 3181061
                    </a>
                    <div className="flex items-center justify-center gap-2 mt-3 text-white/90 text-sm sm:text-base font-medium">
                      <span className="w-3 h-3 rounded-full bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.6)]"></span>
                      Mo.-Sa. 8 - 20 Uhr
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 2. LOKALE EINLEITUNG */}
        <section className="py-20 md:py-32 bg-background">
          <div className="container mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div className="space-y-6">
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold mb-6">
                  Haushaltsauflösung in {page.location}: Was Sie vor Ort
                  erwartet
                </h2>
                <div className="prose prose-lg dark:prose-invert max-w-none text-muted-foreground leading-relaxed">
                  {page.localIntro}
                </div>
              </div>
              <div className="relative w-full">
                <div className="absolute -inset-4 bg-primary/5 rounded-sm transform rotate-2 z-0"></div>
                <img
                  src="https://assets.cdn.filesafe.space/RcAk4NqqJiBX4hgBLewG/media/6a3a901a967e20d627a9db4d.jpg"
                  alt={`Haushaltsauflösung in ${page.location}`}
                  width="600"
                  height="750"
                  loading="lazy"
                  decoding="async"
                  className="relative z-10 w-full h-auto aspect-[4/5] object-cover rounded-sm shadow-xl"
                />
              </div>
            </div>
          </div>
        </section>

        {/* 3. VERTRAUENSSEKTION */}
        <section className="py-20 md:py-32 bg-secondary/30">
          <div className="container mx-auto px-4 md:px-6">
            <div className="max-w-4xl mx-auto text-center space-y-10">
              <p className="text-xl md:text-2xl text-foreground font-medium leading-relaxed max-w-3xl mx-auto">
                {page.trustText}
              </p>
              {page.trustQuote && (
                <div className="pt-8 border-t border-border max-w-2xl mx-auto">
                  <p className="text-lg italic text-muted-foreground font-medium">
                    "{page.trustQuote}"
                  </p>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* 4. LEISTUNGEN IM BEZIRK */}
        <section className="py-20 md:py-32 bg-background">
          <div className="container mx-auto px-4 md:px-6">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-center mb-16">
              Unsere Leistungen in {page.location}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto mb-10">
              {allServices.map((item, i) => (
                <a
                  key={i}
                  href={`/${item.id}`}
                  className="bg-secondary/30 p-8 rounded-sm border border-border shadow-sm hover:shadow-md transition-all group block"
                >
                  <div className="w-12 h-12 bg-primary/10 text-primary rounded-sm flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                    <CheckCircle2 className="h-6 w-6" />
                  </div>
                  <h3 className="text-xl font-bold mb-3">{item.title}</h3>
                  <p className="text-muted-foreground mb-6 line-clamp-2">
                    {item.desc}
                  </p>
                  <div className="text-primary font-bold flex items-center group-hover:translate-x-2 transition-transform">
                    Mehr erfahren <ArrowRight className="ml-2 h-4 w-4" />
                  </div>
                </a>
              ))}
            </div>
            <div className="text-center text-muted-foreground">
              Alle Leistungen auch in den Nachbarbezirken verfügbar:
              {page.neighbors.slice(0, 4).map((n, i) => (
                <span key={i}>
                  {" "}
                  <a
                    href={`/${n.slug}`}
                    className="text-primary hover:underline"
                  >
                    {n.name}
                  </a>
                  {i < Math.min(page.neighbors.length, 4) - 1 ? "," : ""}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* 5. ABLAUF */}
        <section className="py-20 md:py-32 bg-primary text-white relative overflow-hidden">
          {/* Dekorative Hintergrund-Elemente */}
          <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0">
            <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-accent/5 blur-[100px]"></div>
            <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-full bg-white/5 blur-[100px]"></div>
          </div>

          <div className="container mx-auto px-4 md:px-6 relative z-10">
            <div className="text-center max-w-3xl mx-auto mb-16 md:mb-24">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold mb-6 animate-in fade-in slide-in-from-bottom-4 duration-700">
                So läuft eine Haushaltsauflösung in {page.location} ab
              </h2>
              <p className="text-lg text-white/80 animate-in fade-in slide-in-from-bottom-4 duration-700 delay-100 fill-mode-both">
                Schritt für Schritt einfach erklärt – von der ersten Anfrage bis
                zur besenreinen Übergabe.
              </p>
            </div>
            <div className="max-w-5xl mx-auto relative">
              <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-0.5 bg-white/20 transform md:-translate-x-1/2 hidden sm:block"></div>
              <div className="space-y-12 md:space-y-20">
                {[
                  {
                    title: `1. Kostenlose Besichtigung vor Ort in ${page.location}`,
                    desc: "Wir kommen zeitnah zu Ihnen, um uns ein genaues Bild vom Umfang der Arbeiten zu machen und alle Details zu besprechen.",
                  },
                  {
                    title: "2. Verbindliches Festpreisangebot",
                    desc: "Auf Basis der Besichtigung erhalten Sie ein transparentes Angebot mit Festpreisgarantie. Keine versteckten Kosten.",
                  },
                  {
                    title: "3. Haushaltsauflösung zum vereinbarten Termin",
                    desc: "Unser erfahrenes Team arbeitet zügig, diskret und professionell. Wir kümmern uns um alles, inklusive Halteverbot.",
                  },
                  {
                    title: "4. Besenreine Übergabe",
                    desc: "Wir hinterlassen die Räumlichkeiten sauber und übergabefertig für Sie, den Vermieter oder Käufer.",
                  },
                ].map((step, idx) => (
                  <div
                    key={idx}
                    className={`relative flex flex-col sm:flex-row items-center gap-8 md:gap-16 ${idx % 2 === 1 ? "md:flex-row-reverse" : ""} animate-in fade-in slide-in-from-bottom-8 duration-700`}
                    style={{
                      animationDelay: `${(idx + 1) * 150}ms`,
                      animationFillMode: "both",
                    }}
                  >
                    <div className="absolute left-8 md:left-1/2 top-8 md:top-1/2 transform -translate-y-1/2 -translate-x-1/2 w-14 h-14 rounded-full bg-accent text-accent-foreground flex items-center justify-center font-bold text-2xl border-4 border-primary z-10 hidden sm:flex shadow-[0_0_15px_rgba(255,193,7,0.3)] transition-transform hover:scale-110">
                      {idx + 1}
                    </div>
                    <div
                      className={`w-full sm:w-1/2 ${idx % 2 === 1 ? "md:pr-12 lg:pr-16" : "md:pl-12 lg:pl-16"} flex ${idx % 2 === 1 ? "justify-start" : "justify-end"}`}
                    >
                      <div className="bg-white/5 backdrop-blur-sm p-8 rounded-sm border border-white/10 shadow-lg w-full relative hover:bg-white/10 transition-colors duration-300">
                        <div className="flex items-center gap-4 mb-4 sm:hidden">
                          <div className="w-12 h-12 rounded-full bg-accent text-accent-foreground flex items-center justify-center font-bold text-xl border-4 border-primary shadow-sm shrink-0">
                            {idx + 1}
                          </div>
                          <h3 className="text-xl font-bold text-white m-0">
                            {step.title.substring(3)}
                          </h3>
                        </div>
                        <h3 className="text-2xl font-bold text-white mb-4 hidden sm:block">
                          {step.title.substring(3)}
                        </h3>
                        <p className="text-white/80 text-lg">{step.desc}</p>
                      </div>
                    </div>
                    <div className="hidden sm:block w-1/2"></div>
                  </div>
                ))}
              </div>
              <div
                className="mt-20 text-center animate-in fade-in slide-in-from-bottom-4 duration-700"
                style={{ animationDelay: "800ms", animationFillMode: "both" }}
              >
                <Dialog>
                  <DialogTrigger asChild>
                    <Button
                      size="lg"
                      className="h-14 px-8 text-lg font-bold bg-accent text-accent-foreground hover:bg-accent/90 rounded-md shadow-[0_0_20px_rgba(255,193,7,0.3)] transition-transform hover:scale-105"
                    >
                      Jetzt Termin in {page.location} anfragen
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="w-[95vw] max-w-[500px] p-0 overflow-hidden bg-background sm:rounded-xl">
                    <div className="p-5 sm:p-8 max-h-[85vh] overflow-y-auto text-foreground">
                      <DialogHeader className="mb-5">
                        <DialogTitle className="text-xl sm:text-2xl font-bold">
                          Kostenloses Angebot anfordern
                        </DialogTitle>
                      </DialogHeader>
                      <ContactForm />
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
          </div>
        </section>

        <OurWorkSection />

        {/* 6. LOKALE HERAUSFORDERUNGEN */}
        <section className="py-20 md:py-32 bg-background">
          <div className="container mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div className="relative w-full order-2 lg:order-1">
                <div className="absolute -inset-4 bg-primary/5 rounded-sm transform -rotate-2 z-0"></div>
                <img
                  src="https://assets.cdn.filesafe.space/RcAk4NqqJiBX4hgBLewG/media/6a3a901a659bec99fc9f3e99.jpg"
                  alt={`Herausforderungen bei der Haushaltsauflösung in ${page.location}`}
                  width="600"
                  height="750"
                  loading="lazy"
                  decoding="async"
                  className="relative z-10 w-full h-auto aspect-[4/5] object-cover rounded-sm shadow-xl"
                />
              </div>
              <div className="space-y-6 order-1 lg:order-2">
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold mb-6">
                  Besonderheiten bei der Haushaltsauflösung in {page.location}
                </h2>
                <div className="prose prose-lg dark:prose-invert max-w-none text-muted-foreground leading-relaxed">
                  {page.challengesText}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 7. KOSTEN UND FESTPREIS */}
        <section className="py-20 md:py-32 bg-background">
          <div className="container mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              {/* Left Column: Text */}
              <div className="space-y-8">
                <div>
                  <div className="text-primary font-bold tracking-wide uppercase mb-2">
                    Transparente Preise
                  </div>
                  <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground leading-tight">
                    Was kostet eine{" "}
                    <span className="text-primary">Haushaltsauflösung</span> in{" "}
                    {page.location}?
                  </h2>
                </div>
                {getPricingContent(page.location)}
              </div>

              {/* Right Column: Pricing Box */}
              <div className="relative w-full max-w-md mx-auto lg:mx-0 lg:ml-auto">
                <div className="absolute -inset-4 bg-primary/10 rounded-sm transform rotate-2 z-0"></div>
                <div className="relative z-10 bg-primary text-primary-foreground p-8 md:p-10 rounded-sm shadow-xl flex flex-col items-center text-center">
                  <h3 className="text-2xl font-bold mb-4">
                    Haushaltsauflösung
                  </h3>
                  <div className="flex items-baseline gap-2 mb-6">
                    <span className="text-xl font-medium">ab</span>
                    <span className="text-5xl md:text-6xl font-heading font-black text-accent">
                      199 €
                    </span>
                  </div>

                  <p className="font-bold mb-6 opacity-90">abhängig von</p>

                  <ul className="space-y-4 mb-8 w-full text-left">
                    {[
                      "Fläche in QM",
                      "Menge des Hausrats",
                      "Zusatzleistungen (z.B. Bodenbeläge)",
                      "Stockwerk & Aufzug",
                    ].map((item, i) => (
                      <li key={i} className="flex items-center gap-3">
                        <CheckCircle2 className="h-5 w-5 text-green-500 shrink-0" />
                        <span className="font-medium">{item}</span>
                      </li>
                    ))}
                  </ul>

                  <Dialog>
                    <DialogTrigger asChild>
                      <Button
                        size="lg"
                        className="w-full h-14 text-lg font-bold bg-accent text-accent-foreground hover:bg-accent/90 rounded-md shadow-md transition-transform hover:scale-105"
                      >
                        Genaue Kosten anfragen
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="w-[95vw] max-w-[500px] p-0 overflow-hidden bg-background sm:rounded-xl">
                      <div className="p-5 sm:p-8 max-h-[85vh] overflow-y-auto text-foreground">
                        <DialogHeader className="mb-5">
                          <DialogTitle className="text-xl sm:text-2xl font-bold">
                            Kostenloses Angebot anfordern
                          </DialogTitle>
                          <DialogDescription className="text-sm sm:text-base">
                            Hinterlassen Sie uns Ihre Details und wir melden uns
                            umgehend bei Ihnen.
                          </DialogDescription>
                        </DialogHeader>
                        <ContactForm />
                      </div>
                    </DialogContent>
                  </Dialog>

                  <p className="text-sm mt-4 opacity-80">
                    *kostenfrei und unverbindlich
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 8. KUNDENSTIMMEN */}
        {page.reviews && page.reviews.length > 0 && (
          <section className="py-20 md:py-32 bg-secondary/30">
            <div className="container mx-auto px-4 md:px-6">
              <div className="text-center max-w-3xl mx-auto mb-16">
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold mb-6">
                  Das sagen Kunden aus {page.location}
                </h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12 max-w-6xl mx-auto justify-center">
                {page.reviews.map((review, idx) => (
                  <div
                    key={idx}
                    className="bg-background p-8 rounded-sm border border-border shadow-sm flex flex-col"
                  >
                    <div className="flex gap-1 mb-6">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star
                          key={star}
                          className="h-5 w-5 fill-yellow-400 text-yellow-400"
                        />
                      ))}
                    </div>
                    <p className="text-lg font-medium mb-8 leading-relaxed flex-grow">
                      "{review.text}"
                    </p>
                    <div className="font-bold opacity-90">{review.author}</div>
                  </div>
                ))}
              </div>
              <div className="text-center">
                <a
                  href="https://g.page/r/CWrTwxa00xKKEBM/review"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary font-bold hover:underline inline-flex items-center"
                >
                  Alle Bewertungen ansehen{" "}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </a>
              </div>
            </div>
          </section>
        )}

        {/* 9. NACHBARBEZIRKE */}
        <section className="py-20 md:py-32 bg-background">
          <div className="container mx-auto px-4 md:px-6">
            <div className="text-center max-w-3xl mx-auto mb-12">
              <h2 className="text-3xl md:text-4xl font-heading font-bold mb-6">
                Auch in diesen Bezirken für Sie da
              </h2>
            </div>
            <div className="flex flex-wrap justify-center gap-4 max-w-4xl mx-auto mb-8">
              {page.neighbors.map((n, idx) => (
                <a
                  key={idx}
                  href={`/${n.slug}`}
                  className="px-6 py-3 bg-secondary/50 hover:bg-primary hover:text-primary-foreground rounded-full text-base font-bold border border-border transition-colors flex items-center gap-2"
                >
                  <MapPin className="w-4 h-4" />
                  {n.name}
                </a>
              ))}
            </div>
            <div className="text-center text-muted-foreground">
              Miros Haushaltsauflösung ist in ganz Berlin und Umland für Sie da.{" "}
              <a
                href="/#einsatzgebiete"
                className="text-primary hover:underline font-bold"
              >
                Zur Übersicht
              </a>
            </div>
          </div>
        </section>

        {/* 10. FAQ */}
        <section className="py-20 md:py-32 bg-secondary/30">
          <div className="container mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
              {/* Left Column: FAQ Accordion */}
              <div className="space-y-8">
                <div className="space-y-4">
                  <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground leading-tight">
                    Häufige Fragen zur Haushaltsauflösung in {page.location}
                  </h2>
                  <p className="text-lg text-muted-foreground">
                    Hier finden Sie Antworten auf die wichtigsten Fragen rund um
                    unsere Haushaltsauflösungen in {page.location}.
                  </p>
                </div>

                <Accordion
                  type="single"
                  collapsible
                  className="w-full bg-background rounded-sm border border-border p-2 shadow-sm"
                >
                  {page.faqs.map((faq, idx) => (
                    <div key={idx}>
                      <AccordionItem
                        value={`item-${idx}`}
                        className="border-b-0"
                      >
                        <AccordionTrigger className="text-left font-bold text-lg hover:text-primary px-4">
                          {faq.q}
                        </AccordionTrigger>
                        <AccordionContent className="text-muted-foreground text-base px-4 pb-4">
                          {faq.a}
                        </AccordionContent>
                      </AccordionItem>
                      {idx < page.faqs.length - 1 && (
                        <div className="h-px bg-border mx-4"></div>
                      )}
                    </div>
                  ))}
                </Accordion>
              </div>

              {/* Right Column: Image */}
              <div className="relative w-full">
                <div className="absolute -inset-4 bg-primary/5 rounded-sm transform rotate-2 z-0"></div>
                <img
                  src="https://assets.cdn.filesafe.space/RcAk4NqqJiBX4hgBLewG/media/6a3a901a181eb301d4357a32.jpg"
                  alt={`FAQ Haushaltsauflösung ${page.location}`}
                  width="600"
                  height="750"
                  loading="lazy"
                  decoding="async"
                  className="relative z-10 w-full h-auto aspect-[4/5] object-cover rounded-sm shadow-xl"
                />
              </div>
            </div>
          </div>
        </section>

        {/* 11. ABSCHLUSS CTA */}
        <section className="py-20 md:py-32 bg-background">
          <div className="container mx-auto px-4 md:px-6">
            <div className="max-w-4xl mx-auto bg-background rounded-sm border border-border shadow-xl p-8 md:p-12 text-center relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-2 bg-accent"></div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold mb-6">
                Kostenlose Besichtigung in {page.location} anfragen
              </h2>
              <p className="text-lg text-muted-foreground mb-10 max-w-2xl mx-auto">
                Kein Risiko, keine Verpflichtung. Hinterlassen Sie uns Ihre
                Daten und wir melden uns noch am selben Tag bei Ihnen zurück.
              </p>
              <div className="max-w-xl mx-auto text-left">
                <ContactForm />
              </div>
              <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-6 text-muted-foreground">
                <span className="font-medium">
                  Oder rufen Sie uns direkt an:
                </span>
                <a
                  href="tel:015253181061"
                  className="flex items-center gap-2 text-2xl font-bold text-primary hover:text-accent transition-colors"
                >
                  <Phone className="h-6 w-6" /> 01525 3181061
                </a>
              </div>
              <div className="mt-12 pt-8 border-t border-border text-sm font-medium text-muted-foreground flex flex-wrap justify-center gap-4">
                <span>Kostenlose Besichtigung.</span>
                <span>Verbindlicher Festpreis.</span>
                <span>Diskrete Abwicklung.</span>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
